import React from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Plus, Clock, ChevronDown, ChevronRight, Settings, FolderPlus, Globe, Search, Folder, UploadCloud, BookOpen, Database } from 'lucide-react';
import ThreadList from '../chat/ThreadList';
import FolderTree from '../folders/FolderTree';
import CreateFolderModal from '../folders/CreateFolderModal';
import { Folder as FolderType, DataSource } from '../../types';
import { apiService } from '../../services/api';
import ConnectorLogo, { hasConnectorLogo } from '../ConnectorLogo';

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showUnfiled, setShowUnfiled] = React.useState(true);
  const [showCreateFolderModal, setShowCreateFolderModal] = React.useState(false);
  const [globalFolders, setGlobalFolders] = React.useState<FolderType[]>([]);
  const [globalFoldersLoading, setGlobalFoldersLoading] = React.useState(false);
  const [expandedGlobalFolders, setExpandedGlobalFolders] = React.useState<Set<string>>(new Set());
  const [kbFolders, setKbFolders] = React.useState<FolderType[]>([]);
  const [kbFoldersLoading, setKbFoldersLoading] = React.useState(false);
  const [expandedKbFolders, setExpandedKbFolders] = React.useState<Set<string>>(new Set());
  const [dataSources, setDataSources] = React.useState<DataSource[]>([]);
  const [dataSourcesLoading, setDataSourcesLoading] = React.useState(false);
  const [expandedConnectors, setExpandedConnectors] = React.useState<Set<string>>(new Set());

  const isGlobalDocumentsRoute = location.pathname.startsWith('/global-documents');
  const isKnowledgeBaseRoute = location.pathname.startsWith('/knowledge-base');
  const isDataSourcesRoute = location.pathname.startsWith('/data-sources');
  const selectedGlobalFolderId = searchParams.get('folder');
  const selectedKbFolderId = searchParams.get('folder');
  const selectedConnectorType = searchParams.get('connector');
  const selectedDataSourceId = location.pathname.startsWith('/data-sources/') ? location.pathname.split('/')[2] : null;

  const handleNewThread = () => {
    navigate('/chat');
  };

  const handleCreateFolder = () => {
    setShowCreateFolderModal(true);
  };

  React.useEffect(() => {
    if (!isGlobalDocumentsRoute) return;

    let cancelled = false;
    const run = async () => {
      try {
        setGlobalFoldersLoading(true);
        const folders = await apiService.getFolders(false, 'global');
        if (!cancelled) {
          setGlobalFolders(folders);
        }
      } catch (error) {
        if (!cancelled) {
          setGlobalFolders([]);
        }
      } finally {
        if (!cancelled) {
          setGlobalFoldersLoading(false);
        }
      }
    };
    run();

    return () => {
      cancelled = true;
    };
  }, [isGlobalDocumentsRoute]);

  React.useEffect(() => {
    if (!isKnowledgeBaseRoute) return;

    let cancelled = false;
    const run = async () => {
      try {
        setKbFoldersLoading(true);
        const folders = await apiService.getFolders(false, 'knowledge_base');
        if (!cancelled) {
          setKbFolders(folders);
        }
      } catch (error) {
        if (!cancelled) {
          setKbFolders([]);
        }
      } finally {
        if (!cancelled) {
          setKbFoldersLoading(false);
        }
      }
    };
    run();

    return () => {
      cancelled = true;
    };
  }, [isKnowledgeBaseRoute]);

  React.useEffect(() => {
    if (!isDataSourcesRoute) return;
    let cancelled = false;
    const run = async () => {
      try {
        setDataSourcesLoading(true);
        const res = await apiService.getDataSources();
        const list = res.data_sources || [];
        if (!cancelled) {
          setDataSources(list);
          setExpandedConnectors(new Set(Array.from(new Set(list.map((d) => d.connector_type || 'other')))));
        }
      } catch {
        if (!cancelled) setDataSources([]);
      } finally {
        if (!cancelled) setDataSourcesLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [isDataSourcesRoute]);

  const toggleGlobalFolderExpansion = (folderId: string) => {
    setExpandedGlobalFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const setGlobalFolderFilter = (folderId: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (folderId) next.set('folder', folderId);
    else next.delete('folder');
    setSearchParams(next);
  };

  const toggleKbFolderExpansion = (folderId: string) => {
    setExpandedKbFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const setKbFolderFilter = (folderId: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (folderId) next.set('folder', folderId);
    else next.delete('folder');
    setSearchParams(next);
  };

  const triggerGlobalAction = (action: 'create-folder' | 'upload') => {
    const next = new URLSearchParams(searchParams);
    next.set('action', action);
    setSearchParams(next);
  };

  const triggerKbAction = (action: 'create-folder' | 'upload') => {
    const next = new URLSearchParams(searchParams);
    next.set('action', action);
    setSearchParams(next);
  };

  const getConnectorLabel = (connectorType: string): string => {
    if (connectorType === 'google_drive') return 'Connected Google Drives';
    if (connectorType === 'google_search_console') return 'Google Search Console Properties';
    return (connectorType || 'other').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const dataSourcesByConnector = React.useMemo(() => {
    const map: Record<string, DataSource[]> = {};
    dataSources.forEach((ds) => {
      const key = ds.connector_type || 'other';
      if (!map[key]) map[key] = [];
      map[key].push(ds);
    });
    return map;
  }, [dataSources]);

  const connectorTypesSorted = React.useMemo(
    () =>
      Object.keys(dataSourcesByConnector).sort((a, b) =>
        getConnectorLabel(a).localeCompare(getConnectorLabel(b), undefined, { sensitivity: 'base' })
      ),
    [dataSourcesByConnector]
  );

  const setDataSourceConnectorFilter = (connector: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (connector) next.set('connector', connector);
    else next.delete('connector');
    setSearchParams(next);
    navigate('/data-sources');
  };

  const toggleConnectorExpanded = (connectorType: string) => {
    setExpandedConnectors((prev) => {
      const next = new Set(prev);
      if (next.has(connectorType)) next.delete(connectorType);
      else next.add(connectorType);
      return next;
    });
  };

  const renderGlobalFolderTree = (folders: FolderType[], depth = 0): React.ReactNode =>
    folders.map((folder) => {
      const hasChildren = !!folder.children?.length;
      const isExpanded = expandedGlobalFolders.has(folder.id);
      const isSelected = selectedGlobalFolderId === folder.id;

      return (
        <div key={folder.id}>
          <button
            type="button"
            onClick={() => setGlobalFolderFilter(folder.id)}
            className={`w-full flex items-center gap-1.5 rounded px-2 py-1.5 text-xs text-left hover:bg-gray-100 ${
              isSelected ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
            }`}
            style={{ paddingLeft: `${depth * 14 + 8}px` }}
          >
            {hasChildren ? (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGlobalFolderExpansion(folder.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleGlobalFolderExpansion(folder.id);
                  }
                }}
                className="inline-flex"
              >
                {isExpanded ? (
                  <ChevronDown className="h-3 w-3 text-gray-500" />
                ) : (
                  <ChevronRight className="h-3 w-3 text-gray-500" />
                )}
              </span>
            ) : (
              <span className="w-3" />
            )}
            <Folder className="h-3 w-3 text-gray-500 flex-shrink-0" />
            <span className="truncate flex-1">{folder.name}</span>
            {!!folder.document_count && <span className="text-[10px] text-gray-500">({folder.document_count})</span>}
          </button>
          {hasChildren && isExpanded && renderGlobalFolderTree(folder.children || [], depth + 1)}
        </div>
      );
    });

  const renderKbFolderTree = (folders: FolderType[], depth = 0): React.ReactNode =>
    folders.map((folder) => {
      const hasChildren = !!folder.children?.length;
      const isExpanded = expandedKbFolders.has(folder.id);
      const isSelected = selectedKbFolderId === folder.id;

      return (
        <div key={folder.id}>
          <button
            type="button"
            onClick={() => setKbFolderFilter(folder.id)}
            className={`w-full flex items-center gap-1.5 rounded px-2 py-1.5 text-xs text-left hover:bg-gray-100 ${
              isSelected ? 'bg-amber-50 text-amber-800' : 'text-gray-700'
            }`}
            style={{ paddingLeft: `${depth * 14 + 8}px` }}
          >
            {hasChildren ? (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleKbFolderExpansion(folder.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleKbFolderExpansion(folder.id);
                  }
                }}
                className="inline-flex"
              >
                {isExpanded ? (
                  <ChevronDown className="h-3 w-3 text-gray-500" />
                ) : (
                  <ChevronRight className="h-3 w-3 text-gray-500" />
                )}
              </span>
            ) : (
              <span className="w-3" />
            )}
            <BookOpen className="h-3 w-3 text-amber-600 flex-shrink-0" />
            <span className="truncate flex-1">{folder.name}</span>
            {!!folder.document_count && <span className="text-[10px] text-gray-500">({folder.document_count})</span>}
          </button>
          {hasChildren && isExpanded && renderKbFolderTree(folder.children || [], depth + 1)}
        </div>
      );
    });

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="p-2 border-b border-gray-200 flex-shrink-0">
        {/* Search */}
        <div className="mb-2">
          <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5">
            <Search className="h-3.5 w-3.5 text-gray-500" />
            <span className="flex-1 text-xs text-gray-500">Search...</span>
            <span className="rounded border border-gray-300 bg-white px-1 py-0.5 text-[10px] text-gray-500">Ctrl+/</span>
          </div>
        </div>

      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto flex flex-col min-h-0">
        {isGlobalDocumentsRoute ? (
          <div className="px-2 py-2">
            <div className="px-2 pb-2 flex gap-1.5">
              <button
                type="button"
                onClick={() => triggerGlobalAction('upload')}
                className="flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded text-xs font-normal bg-primary-600 text-white hover:bg-primary-700 whitespace-nowrap"
              >
                <UploadCloud className="h-3.5 w-3.5" />
                New Upload
              </button>
              <button
                type="button"
                onClick={() => triggerGlobalAction('create-folder')}
                className="flex items-center gap-1 py-1.5 px-2 rounded text-xs font-normal border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <FolderPlus className="h-3.5 w-3.5" />
                New Folder
              </button>
            </div>
            <button
              type="button"
              onClick={() => setGlobalFolderFilter(null)}
              className={`w-full flex items-center gap-1.5 rounded px-2 py-1.5 text-xs text-left hover:bg-gray-100 ${
                !selectedGlobalFolderId ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
              }`}
            >
              <Globe className="h-3 w-3 text-gray-500" />
              <span className="truncate flex-1">All Documents</span>
            </button>
            <div className="mt-1">
              {globalFoldersLoading ? (
                <div className="px-2 py-3 text-xs text-gray-500">Loading folders...</div>
              ) : globalFolders.length ? (
                renderGlobalFolderTree(globalFolders)
              ) : (
                <div className="px-2 py-3 text-xs text-gray-500">No global folders</div>
              )}
            </div>
          </div>
        ) : isKnowledgeBaseRoute ? (
          <div className="px-2 py-2">
            <div className="px-2 pb-2 flex gap-1.5">
              <button
                type="button"
                onClick={() => triggerKbAction('upload')}
                className="flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded text-xs font-normal bg-amber-600 text-white hover:bg-amber-700 whitespace-nowrap"
              >
                <UploadCloud className="h-3.5 w-3.5" />
                New Upload
              </button>
              <button
                type="button"
                onClick={() => triggerKbAction('create-folder')}
                className="flex items-center gap-1 py-1.5 px-2 rounded text-xs font-normal border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <FolderPlus className="h-3.5 w-3.5" />
                New Folder
              </button>
            </div>
            <button
              type="button"
              onClick={() => setKbFolderFilter(null)}
              className={`w-full flex items-center gap-1.5 rounded px-2 py-1.5 text-xs text-left hover:bg-gray-100 ${
                !selectedKbFolderId ? 'bg-amber-50 text-amber-800' : 'text-gray-700'
              }`}
            >
              <BookOpen className="h-3 w-3 text-amber-600" />
              <span className="truncate flex-1">All Documents</span>
            </button>
            <div className="mt-1">
              {kbFoldersLoading ? (
                <div className="px-2 py-3 text-xs text-gray-500">Loading folders...</div>
              ) : kbFolders.length ? (
                renderKbFolderTree(kbFolders)
              ) : (
                <div className="px-2 py-3 text-xs text-gray-500">No knowledge folders</div>
              )}
            </div>
          </div>
        ) : isDataSourcesRoute ? (
          <div className="px-2 py-2">
            <div className="px-2 pb-2 flex gap-1.5">
              <button
                type="button"
                onClick={() => navigate('/data-sources/add')}
                className="flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded text-xs font-normal bg-primary-600 text-white hover:bg-primary-700 whitespace-nowrap"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Data Source
              </button>
            </div>

            <button
              type="button"
              onClick={() => setDataSourceConnectorFilter(null)}
              className={`w-full flex items-center gap-1.5 rounded px-2 py-1.5 text-xs text-left hover:bg-gray-100 ${
                !selectedDataSourceId && !selectedConnectorType ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
              }`}
            >
              <Database className="h-3 w-3 text-primary-600" />
              <span className="truncate flex-1">All Data Sources</span>
              <span className="text-[10px] text-gray-500">({dataSources.length})</span>
            </button>

            <div className="mt-1">
              {dataSourcesLoading ? (
                <div className="px-2 py-3 text-xs text-gray-500">Loading sources...</div>
              ) : connectorTypesSorted.length ? (
                connectorTypesSorted.map((connectorType) => {
                  const list = dataSourcesByConnector[connectorType] || [];
                  const isExpanded = expandedConnectors.has(connectorType);
                  const isConnectorSelected = !selectedDataSourceId && selectedConnectorType === connectorType;
                  return (
                    <div key={connectorType} className="mb-1">
                      <div
                        className={`flex items-center gap-1.5 rounded px-2 py-1.5 text-xs cursor-pointer ${
                          isConnectorSelected ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest('button')) return;
                          setDataSourceConnectorFilter(connectorType);
                        }}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleConnectorExpanded(connectorType);
                          }}
                          className="inline-flex rounded hover:bg-gray-200"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-3 w-3 text-gray-500" />
                          ) : (
                            <ChevronRight className="h-3 w-3 text-gray-500" />
                          )}
                        </button>
                        {hasConnectorLogo(connectorType) ? (
                          <ConnectorLogo connectorType={connectorType} size={12} className="flex-shrink-0" alt="" />
                        ) : (
                          <Folder className="h-3 w-3 text-gray-500" />
                        )}
                        <span className="truncate flex-1">{getConnectorLabel(connectorType)}</span>
                        <span className="text-[10px] text-gray-500">({list.length})</span>
                      </div>
                      {isExpanded && (
                        <div className="ml-4 border-l border-gray-200 pl-1">
                          {list.map((ds) => {
                            const isSelected = selectedDataSourceId === ds.id;
                            return (
                              <button
                                key={ds.id}
                                type="button"
                                onClick={() => navigate(`/data-sources/${ds.id}`)}
                                className={`mt-0.5 w-full flex items-center gap-1.5 rounded px-2 py-1.5 text-xs text-left ${
                                  isSelected ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                {ds.connector_type && hasConnectorLogo(ds.connector_type) ? (
                                  <ConnectorLogo connectorType={ds.connector_type} size={11} className="flex-shrink-0" alt="" />
                                ) : (
                                  <Folder className="h-3 w-3 text-gray-400" />
                                )}
                                <span className="truncate flex-1">{ds.display_name}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="px-2 py-3 text-xs text-gray-500">No data sources</div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Compact actions */}
            <div className="px-2 py-1.5 flex gap-1.5">
              <button
                type="button"
                onClick={handleNewThread}
                className="flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded text-xs font-normal bg-primary-600 text-white hover:bg-primary-700"
              >
                <Plus className="h-3.5 w-3.5" />
                New Thread
              </button>
              <button
                type="button"
                onClick={handleCreateFolder}
                className="flex items-center gap-1 py-1.5 px-2 rounded text-xs font-normal border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <FolderPlus className="h-3.5 w-3.5" />
                Folder
              </button>
            </div>

            {/* Folder tree - direct, no "My Data" wrapper */}
            <div className="px-2 pt-1">
              <FolderTree />
            </div>

            {/* Your Chats Section */}
            <div className="px-2 py-1.5 border-t border-gray-200">
              <button
                onClick={() => setShowUnfiled(!showUnfiled)}
                className="w-full flex items-center justify-between px-2 py-1 hover:bg-gray-100 rounded text-xs font-normal text-gray-700"
              >
                <div className="flex items-center space-x-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  <span>Your Chats</span>
                </div>
                {showUnfiled ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>

              {showUnfiled && (
                <div className="mt-1">
                  <ThreadList />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Settings - Always at bottom */}
      <div className="border-t border-gray-200 bg-white flex-shrink-0">
        <button
          onClick={() => {
            // TODO: Open settings modal or navigate to settings
            console.log('Settings clicked');
          }}
          className="w-full flex items-center space-x-1.5 px-3 py-2 hover:bg-gray-100 text-xs font-normal text-gray-700"
        >
          <Settings className="h-4 w-4" />
          <span>Settings</span>
        </button>
      </div>

      {/* Create Folder Modal */}
      <CreateFolderModal
        isOpen={showCreateFolderModal}
        onClose={() => setShowCreateFolderModal(false)}
      />
    </div>
  );
};

export default Sidebar;
